import { useMemo, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm, type Resolver } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';

import {
  Box,
  Button,
  HeroCard,
  Icon,
  Stepper,
  Text,
  TextInput,
  ValidatePassword,
} from '~/atomic';
import { useAppDispatch } from '~/store';
import { registerUser } from '~/store/register/thunks';
import type { propsRegisterActivity } from '~/store/register/interfaces';
import { isValidDocument } from '~/utils/validate';

interface FormData {
  name: string;
  email: string;
  phone: string;
  document: string;
  farmName: string;
  activity: propsRegisterActivity;
  hectares?: string;
  livestockHeadCount?: string;
  annualRevenue: string;
  password: string;
  passwordConfirm: string;
  acceptedTerms: boolean;
}

type StepField = keyof FormData;

const TERMS_URL = 'https://pagaagro.com.br/termos-e-politica';

const STEPS = ['Conta', 'Identidade', 'Fazenda', 'Segurança'];
const TOTAL_STEPS = STEPS.length;

const STEP_TITLES: { title: string; subtitle: string }[] = [
  { title: 'Vamos nos conhecer', subtitle: 'Conte um pouco sobre você.' },
  {
    title: 'Sobre sua fazenda',
    subtitle: 'Conte mais sobre sua propriedade.',
  },
  { title: 'Sua atividade', subtitle: 'Como é a sua produção?' },
  {
    title: 'Defina sua senha',
    subtitle: 'Crie uma senha segura para acessar sua conta.',
  },
];

const STEP_FIELDS: StepField[][] = [
  ['name', 'email', 'phone'],
  ['farmName', 'document'],
  ['activity', 'hectares', 'livestockHeadCount', 'annualRevenue'],
  ['password', 'passwordConfirm', 'acceptedTerms'],
];

const schema = yup.object({
  name: yup.string().trim().required('Informe seu nome'),
  email: yup
    .string()
    .trim()
    .email('E-mail inválido')
    .required('Informe seu e-mail'),
  phone: yup
    .string()
    .test('is-phone', 'Telefone inválido', (value) => {
      if (!value) return false;
      const digits = value.replace(/\D/g, '');
      return digits.length === 10 || digits.length === 11;
    })
    .required('Informe seu telefone'),
  farmName: yup.string().trim().required('Informe o nome da fazenda'),
  document: yup
    .string()
    .required('Informe seu CPF ou CNPJ')
    .test('is-document-length', 'Informe um CPF (11 dígitos) ou CNPJ (14 dígitos)', (value) => {
      if (!value) return false;
      const digits = value.replace(/\D/g, '');
      return digits.length === 11 || digits.length === 14;
    })
    .test('is-document-valid', 'CPF ou CNPJ inválido', (value) => {
      if (!value) return false;
      return isValidDocument(value);
    }),
  activity: yup
    .mixed<propsRegisterActivity>()
    .oneOf(['agriculture', 'livestock'], 'Selecione sua atividade')
    .required('Selecione sua atividade'),
  hectares: yup.string().when('activity', {
    is: 'agriculture',
    then: (s) => s.required('Informe os hectares cultivados'),
    otherwise: (s) => s.notRequired(),
  }),
  livestockHeadCount: yup.string().when('activity', {
    is: 'livestock',
    then: (s) => s.required('Informe o tamanho do rebanho'),
    otherwise: (s) => s.notRequired(),
  }),
  annualRevenue: yup.string().required('Informe o faturamento anual'),
  password: yup
    .string()
    .min(8, 'Mínimo de 8 caracteres')
    .required('Informe sua senha'),
  passwordConfirm: yup
    .string()
    .oneOf([yup.ref('password')], 'As senhas precisam ser iguais')
    .required('Confirme sua senha'),
  acceptedTerms: yup
    .boolean()
    .oneOf([true], 'É necessário aceitar os Termos de Uso e a Política de Privacidade')
    .required('É necessário aceitar os Termos de Uso e a Política de Privacidade'),
});

function ActivityOption({
  label,
  description,
  selected,
  onClick,
}: {
  label: string;
  description: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex-1 text-left rounded-medium border p-small transition-colors ${
        selected
          ? 'bg-success-01 border-primary-01'
          : 'bg-intense-02 border-intense-03 hover:bg-intense-03'
      }`}
    >
      <Text
        className={`text-paragraphMD ${
          selected ? 'color-primary-01' : 'color-complement-01'
        }`}
      >
        {label}
      </Text>
      <Text className="mt-quark text-bodySM color-intense-05">{description}</Text>
    </button>
  );
}

export default function RegisterView() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);

  const {
    control,
    watch,
    trigger,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(schema) as unknown as Resolver<FormData>,
    mode: 'onTouched',
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      document: '',
      farmName: '',
      activity: 'agriculture',
      hectares: '',
      livestockHeadCount: '',
      annualRevenue: '',
      password: '',
      passwordConfirm: '',
      acceptedTerms: false,
    },
  });

  const activity = watch('activity');
  const password = watch('password');

  const currentTitle = useMemo(
    () => STEP_TITLES[Math.min(step, TOTAL_STEPS - 1)],
    [step],
  );

  const buttonText =
    step === TOTAL_STEPS - 1 ? 'Finalizar cadastro' : 'Continuar';

  const back = () => {
    if (step === 0) {
      navigate(-1);
      return;
    }
    setStep((s) => s - 1);
  };

  const submit = handleSubmit(async (values) => {
    setLoading(true);
    try {
      const ok = await dispatch(
        registerUser({
          name: values.name,
          email: values.email,
          phone: values.phone,
          document: values.document,
          farmName: values.farmName,
          activity: values.activity,
          hectares: values.hectares,
          livestockHeadCount: values.livestockHeadCount,
          annualRevenue: values.annualRevenue,
          password: values.password,
        }),
      ).unwrap();
      if (ok) navigate('/app/home', { replace: true });
    } finally {
      setLoading(false);
    }
  });

  const next = async () => {
    const fields = STEP_FIELDS[step];
    const valid = await trigger(fields);
    if (!valid) return;

    if (step < TOTAL_STEPS - 1) {
      setStep((s) => s + 1);
      return;
    }
    await submit();
  };

  const handleSelectActivity = (value: propsRegisterActivity) => {
    setValue('activity', value, { shouldValidate: false });
  };

  return (
    <div className="min-h-screen flex flex-col bg-settings-screen">
      <HeroCard className="pt-small pb-medium rounded-b-large">
        <div className="w-full xl:max-w-[480px] xl:mx-auto px-small pt-small">
          <button
            type="button"
            onClick={back}
            aria-label="Voltar"
            className="h-[40px] w-[40px] rounded-[10px] bg-intense-01/15 flex items-center justify-center hover:bg-intense-01/25 transition-colors"
          >
            <Icon name="icon-arrow-left" size={20} color="#FFFFFF" />
          </button>
        </div>
        <div className="w-full px-small pb-small mt-small xl:max-w-[480px] xl:mx-auto">
          <Text className="text-captionLG color-intense-01 uppercase tracking-wider">
            {`Passo ${Math.min(step + 1, TOTAL_STEPS)} de ${TOTAL_STEPS}`}
          </Text>
          <Text className="mt-smaller text-display1 color-intense-01" as="p">
            {currentTitle.title}
          </Text>
          <Text className="mt-nano text-bodySM color-intense-01">
            {currentTitle.subtitle}
          </Text>
          <Stepper
            className="mt-medium"
            current={step}
            total={TOTAL_STEPS}
            labels={STEPS}
          />
        </div>
      </HeroCard>

      <main className="flex-1 w-full xl:max-w-[480px] xl:mx-auto flex flex-col px-small">
        <section className="mt-medium rounded-large bg-intense-01 border border-intense-03 p-small md:p-medium shadow-level1">
          {step === 0 && (
            <Box className="flex flex-col gap-small">
              <TextInput
                control={control}
                name="name"
                icon="icon-user"
                placeholder="Nome completo"
                autoCapitalize="words"
                errorMessage={errors.name?.message}
              />
              <Box className="flex flex-col gap-quark">
                <TextInput
                  control={control}
                  name="email"
                  icon="icon-email"
                  placeholder="E-mail"
                  keyboardType="email-address"
                  autoComplete="email"
                  mask="email"
                  errorMessage={errors.email?.message}
                />
                <Text className="text-captionMD color-intense-05">
                  Usaremos para enviar alertas de vencimento.
                </Text>
              </Box>
              <TextInput
                control={control}
                name="phone"
                icon="icon-phone"
                placeholder="Telefone (WhatsApp)"
                keyboardType="phone-pad"
                mask="phone"
                errorMessage={errors.phone?.message}
              />
            </Box>
          )}

          {step === 1 && (
            <Box className="flex flex-col gap-small">
              <TextInput
                control={control}
                name="farmName"
                icon="icon-enterprise"
                placeholder="Nome da fazenda"
                errorMessage={errors.farmName?.message}
              />
              <TextInput
                control={control}
                name="document"
                icon="icon-document"
                placeholder="CPF ou CNPJ"
                keyboardType="numeric"
                mask="document"
                errorMessage={errors.document?.message}
              />
            </Box>
          )}

          {step === 2 && (
            <Box className="flex flex-col gap-small">
              <Box>
                <Text className="mb-smaller text-bodyLG color-complement-01">
                  Qual é a sua atividade principal?
                </Text>
                <div className="flex flex-col sm:flex-row gap-smaller">
                  <ActivityOption
                    label="Agricultura"
                    description="Lavoura e cultivo"
                    selected={activity === 'agriculture'}
                    onClick={() => handleSelectActivity('agriculture')}
                  />
                  <ActivityOption
                    label="Pecuária"
                    description="Criação de animais"
                    selected={activity === 'livestock'}
                    onClick={() => handleSelectActivity('livestock')}
                  />
                </div>
                {errors.activity?.message ? (
                  <Text className="mt-quark text-captionMD color-warning-06">
                    {errors.activity.message}
                  </Text>
                ) : null}
              </Box>

              {activity === 'agriculture' && (
                <TextInput
                  control={control}
                  name="hectares"
                  icon="icon-grid"
                  placeholder="Hectares cultivados"
                  keyboardType="numeric"
                  mask="integer"
                  errorMessage={errors.hectares?.message}
                />
              )}

              {activity === 'livestock' && (
                <TextInput
                  control={control}
                  name="livestockHeadCount"
                  icon="icon-weight"
                  placeholder="Cabeças no rebanho"
                  keyboardType="numeric"
                  mask="integer"
                  errorMessage={errors.livestockHeadCount?.message}
                />
              )}

              <TextInput
                control={control}
                name="annualRevenue"
                icon="icon-doublecoins"
                placeholder="Faturamento anual (R$)"
                keyboardType="numeric"
                mask="money"
                errorMessage={errors.annualRevenue?.message}
              />
            </Box>
          )}

          {step === 3 && (
            <Box className="flex flex-col gap-small">
              <TextInput
                control={control}
                name="password"
                icon="icon-security"
                placeholder="Senha"
                secureTextEntry
                autoComplete="new-password"
                errorMessage={errors.password?.message}
              />
              <ValidatePassword value={password || ''} />
              <TextInput
                control={control}
                name="passwordConfirm"
                icon="icon-security"
                placeholder="Confirme sua senha"
                secureTextEntry
                errorMessage={errors.passwordConfirm?.message}
              />

              <Controller
                control={control}
                name="acceptedTerms"
                render={({ field: { value, onChange } }) => (
                  <Box className="flex flex-col gap-quark">
                    <button
                      type="button"
                      role="checkbox"
                      aria-checked={value}
                      onClick={() => onChange(!value)}
                      className="flex items-start gap-smaller text-left"
                    >
                      <span
                        className={`mt-quark flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-[6px] border transition-colors ${
                          value
                            ? 'bg-primary-01 border-primary-01'
                            : 'bg-intense-02 border-intense-04'
                        }`}
                      >
                        {value && (
                          <Icon name="icon-circle-check" size={16} color="#FFFFFF" />
                        )}
                      </span>
                      <Text inline className="text-bodySM color-intense-06">
                        Li e aceito os{' '}
                        <a
                          href={TERMS_URL}
                          target="_blank"
                          rel="noreferrer noopener"
                          className="color-primary-01 text-bodyLG underline"
                          onClick={(e) => e.stopPropagation()}
                        >
                          Termos de Uso e a Política de Privacidade
                        </a>
                        .
                      </Text>
                    </button>
                    {errors.acceptedTerms?.message ? (
                      <Text className="text-captionMD color-warning-06">
                        {errors.acceptedTerms.message}
                      </Text>
                    ) : null}
                  </Box>
                )}
              />
            </Box>
          )}
        </section>

        <div className="mt-medium mb-medium">
          <Button
            onClick={next}
            loading={loading}
            fullWidth
            rightIcon={
              step === TOTAL_STEPS - 1 ? 'icon-circle-check' : 'icon-arrow-right'
            }
            text={buttonText}
          />
          {step === 0 && (
            <Text
              className="mt-medium w-full text-center color-intense-05 text-bodyMD"
              as="p"
            >
              Já tem conta?{' '}
              <Text
                inline
                className="color-primary-01 text-bodyLG underline"
                onClick={() => navigate('/auth/login')}
              >
                Entrar
              </Text>
            </Text>
          )}
        </div>
      </main>
    </div>
  );
}
